import { ReactNode } from "react";
import { Card, Col, Image, Row, Tag, Typography } from "antd";
import { EventData, InclusionData, TicketData } from "../../models/event";
import { CustomFormSection } from "../../pages/custom-form/types";
import { getEventVenueLabel } from "../../utils/eventDisplay";
import getCurrencyWithSymbol from "../../utils/getCurrencySymbol";
import {
  dateTimeDisplayFormat,
  formatDisplayDate,
  formatDisplayTime,
} from "../../utils/DateAndTimeConvertor";
import { combineDateAndTime } from "../../utils/DateFn";
import styles from "./event-setup-review.module.scss";
import ReviewFieldGrid, { ReviewField } from "./ReviewFieldGrid";

const { Text, Paragraph, Title } = Typography;

const CARD_HEAD_STYLES = {
  header: { background: "#fafafa", borderBottom: "1px solid #f0f0f0" },
};

const TIME_PARSE_FORMATS = ["hh:mm A", "h:mm A", "HH:mm:ss", "HH:mm"] as const;

function mediaUrl(
  value: EventData["banner_image"] | EventData["feature_images"],
): string | undefined {
  if (typeof value === "string" && value.trim()) return value;
  if (Array.isArray(value) && value.length > 0) {
    const first = value[0] as { url?: string; thumbnail?: string };
    return first?.url || first?.thumbnail;
  }
  return undefined;
}

/** Same format as sidebar: Fri, 29 May 2026, 12:30 PM */
export function formatReviewDateTime(
  date?: string | null,
  time?: string | null,
): string {
  if (!date) return "N/A";

  const combined = combineDateAndTime(date, time ?? undefined);
  if (combined?.isValid()) {
    const datePart = formatDisplayDate(combined);
    if (!time?.trim()) return datePart;
    const timePart = formatDisplayTime(combined, [...TIME_PARSE_FORMATS]);
    return `${datePart}, ${timePart}`;
  }

  const fallback = dateTimeDisplayFormat(date, time ?? undefined);
  return fallback === "Invalid date/time" ? formatDisplayDate(date) : fallback;
}

function humanize(value?: string | null): string {
  if (!value) return "N/A";
  return value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function ageRange(min?: number | null, max?: number | null): string {
  if (min != null && max != null) return `${min}\u00a0–\u00a0${max}\u00a0years`;
  if (min != null) return `${min}+\u00a0years`;
  if (max != null) return `Up to\u00a0${max}\u00a0years`;
  return "N/A";
}

function formatPrice(currency: string, amount: number): string {
  const formatted = getCurrencyWithSymbol(currency, amount);
  return formatted.replace(/ /g, "\u00a0");
}

function linkValue(url?: string | null) {
  return url?.trim() ? (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className={styles.linkValue}
    >
      {url}
    </a>
  ) : (
    "—"
  );
}

function getPrimaryLink(event: EventData): string | undefined {
  return (
    event.social_media_link?.trim() ||
    event.faq_link?.trim() ||
    event.video_url?.trim() ||
    undefined
  );
}

function buildPolicyFields(event: EventData): ReviewField[] {
  const termsValue = event.terms_link?.trim()
    ? linkValue(event.terms_link)
    : event.terms_and_conditions?.trim()
      ? (
          <span
            className={styles.wrapValue}
            dangerouslySetInnerHTML={{ __html: event.terms_and_conditions }}
          />
        )
      : "—";

  const fields: ReviewField[] = [
    {
      label: "Terms & conditions",
      value: termsValue,
    },
    {
      label: "Rules & regulations",
      value: event.rules ? (
        <span
          className={styles.wrapValue}
          dangerouslySetInnerHTML={{ __html: event.rules }}
        />
      ) : (
        "—"
      ),
    },
  ];

  const primaryLink = getPrimaryLink(event);
  if (primaryLink) {
    fields.push({
      label: "Social media link",
      value: linkValue(primaryLink),
    });
  }

  return fields;
}

function inclusionLabels(inclusions?: InclusionData[]): string {
  if (!inclusions?.length) return "N/A";
  return inclusions
    .map((i) => i.name)
    .filter(Boolean)
    .join(", ");
}

function nowrap(value: ReactNode) {
  return <span className={styles.nowrapValue}>{value}</span>;
}

function buildTicketFields(
  ticket: TicketData,
  eventCurrency?: string,
): ReviewField[] {
  const formatDistance = (distance?: string | null) => {
    if (!distance) return "N/A";

    const value = distance.toString().trim();
    const lowerValue = value.toLowerCase();
    if (lowerValue.endsWith("km") || lowerValue.endsWith("mtr")) {
      return value;
    }

    if (value.toLowerCase().endsWith("k")) {
      return `${value.slice(0, -1)} KM`;
    }

    if (value.toLowerCase().endsWith("m")) {
      return `${value.slice(0, -1)} MTR`;
    }

    return value;
  };
  const currency = ticket.currency || eventCurrency || "INR";
  const fields: ReviewField[] = [
    {
      label: "Price",
      value: nowrap(formatPrice(currency, Number(ticket.price ?? 0))),
    },
    {
      label: "Total quantity",
      value: nowrap(ticket.quantity ?? "N/A"),
    },
    {
      label: "Activity type",
      value: humanize(ticket.activity_type),
    },
    {
      label: "Distance",
      value: formatDistance(ticket.distance),
    },
  ];

  fields.push(
    {
      label: "Min tickets per order",
      value: nowrap(ticket.min_per_transaction ?? "N/A"),
    },
    {
      label: "Max tickets per order",
      value: nowrap(ticket.max_per_transaction ?? "N/A"),
    },
    {
      label: "Age range",
      value: nowrap(ageRange(ticket.min_age, ticket.max_age)),
    },
    {
      label: "Registration opens",
      value: nowrap(
        formatReviewDateTime(
          ticket.registration_start_date,
          ticket.registration_start_time,
        ),
      ),
    },
    {
      label: "Registration closes",
      value: nowrap(
        formatReviewDateTime(
          ticket.registration_end_date,
          ticket.registration_end_time,
        ),
      ),
    },
  );

  return fields;
}

function TicketReviewCard({
  ticket,
  eventCurrency,
  eventType,
  index,
}: {
  ticket: TicketData;
  eventCurrency?: string;
  eventType?: string;
  index: number;
}) {
  const statusColorMap = {
    active: "green",
    inactive: "default",
    draft: "gold",
    sold_out: "volcano",
    closed: "red",
  };

  return (
    <Card
      size="small"
      className={`${styles.reviewSectionCard} ${styles.ticketCard}`}
      styles={CARD_HEAD_STYLES}
      title={
        <span className="flex flex-wrap items-center gap-8">
          <span className={styles.ticketCardTitle}>
            {ticket.name || `Ticket ${index + 1}`}
          </span>
          {ticket.status && (
            <Tag
              className="!m-0 capitalize"
              color={statusColorMap[ticket?.status] || "default"}
            >
              {ticket?.status?.replace("_", " ")}
            </Tag>
          )}
          {ticket.requires_approval && (
            <Tag color="orange" className="!m-0">
              Approval required
            </Tag>
          )}
        </span>
      }
    >
      <ReviewFieldGrid fields={buildTicketFields(ticket, eventCurrency)} />

      {eventType === "hybrid" && ticket.event_type && (
        <div className={styles.descriptionBlock}>
          <span className={styles.descriptionLabel}>Ticket format</span>
          <Text>{humanize(ticket.event_type)}</Text>
        </div>
      )}

      {ticket.inclusions?.length ? (
        <div className={styles.descriptionBlock}>
          <span className={styles.descriptionLabel}>Inclusions</span>
          <Text>{inclusionLabels(ticket.inclusions)}</Text>
        </div>
      ) : null}

      {ticket.description?.trim() && (
        <div className={styles.descriptionBlock}>
          <span className={styles.descriptionLabel}>Ticket description</span>
          <Paragraph style={{ marginBottom: 0 }}>
            <span dangerouslySetInnerHTML={{ __html: ticket.description }} />
          </Paragraph>
        </div>
      )}
    </Card>
  );
}

type Props = {
  event: EventData;
};

const EventSetupReviewSummary = ({ event }: Props) => {
  const bannerSrc = mediaUrl(event.banner_image);
  const featureImages = Array.isArray(event.feature_images)
    ? (event.feature_images as { url?: string; thumbnail?: string }[])
    : [];

  const visibility =
    (event as EventData & { visibility?: string }).visibility ??
    event.visiblity;

  const basicFields: ReviewField[] = [
    { label: "Event name", value: event.name || "N/A" },
    { label: "Event type", value: humanize(event.event_type) },
    ...(event.event_type === "online" || event.event_type === "hybrid"
      ? [{ label: "Meeting link", value: linkValue(event.virtual_link) }]
      : []),
    { label: "Visibility", value: humanize(visibility) },
    { label: "Venue", value: getEventVenueLabel(event) },
    {
      label: "Start date and time",
      value: nowrap(formatReviewDateTime(event.start_date, event.start_time)),
    },
    {
      label: "End date and time",
      value: nowrap(formatReviewDateTime(event.end_date, event.end_time)),
    },
  ];

  const policyFields = buildPolicyFields(event);

  return (
    <div className="flex flex-col gap-16">
      <Card
        title="Basic information"
        size="small"
        className={styles.reviewSectionCard}
        styles={CARD_HEAD_STYLES}
      >
        <ReviewFieldGrid fields={basicFields} />
        {event.description && (
          <div className={styles.descriptionBlock}>
            <span className={styles.descriptionLabel}>Event description</span>
            <Paragraph style={{ marginBottom: 0 }}>
              <span dangerouslySetInnerHTML={{ __html: event.description }} />
            </Paragraph>
          </div>
        )}
      </Card>

      <Card
        title="Banners & media"
        size="small"
        className={styles.reviewSectionCard}
        styles={CARD_HEAD_STYLES}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} md={bannerSrc ? 10 : 24}>
            {bannerSrc ? (
              <Image
                src={bannerSrc}
                alt="Event banner"
                style={{
                  width: "100%",
                  maxHeight: 200,
                  objectFit: "cover",
                  borderRadius: 8,
                }}
              />
            ) : (
              <Text type="secondary">No banner uploaded</Text>
            )}
          </Col>
          {featureImages.length > 0 && (
            <Col xs={24} md={14}>
              <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
                Feature images
              </Text>
              <div className="flex flex-wrap gap-8 mt-8">
                {featureImages.map((img, i) => (
                  <Image
                    key={img.url || i}
                    src={img.url || img.thumbnail}
                    width={72}
                    height={72}
                    style={{ objectFit: "cover", borderRadius: 6 }}
                  />
                ))}
              </div>
            </Col>
          )}
        </Row>
      </Card>

      <Card
        title="Policies & links"
        size="small"
        className={styles.reviewSectionCard}
        styles={CARD_HEAD_STYLES}
      >
        <ReviewFieldGrid fields={policyFields} onePerRow />
      </Card>

      {event.form_config && Array.isArray(event.form_config) && event.form_config.length > 0 && (
        <Card
          title="Registration Form Fields"
          size="small"
          className={styles.reviewSectionCard}
          styles={CARD_HEAD_STYLES}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {(event.form_config as CustomFormSection[]).map((section) => {
              const enabledFields = section.fields?.filter((field) => field.enabled) ?? [];
              if (enabledFields.length === 0) return null;

              const reviewFields = enabledFields.map((field) => ({
                label: field.label,
                value: (field.fixedRequired || field.required) ? (
                  <Tag color="red" style={{ margin: 0, fontSize: 10, lineHeight: "18px", paddingInline: 6 }}>Mandatory</Tag>
                ) : (
                  <Tag style={{ margin: 0, fontSize: 10, lineHeight: "18px", paddingInline: 6 }}>Optional</Tag>
                ),
              }));

              return (
                <div key={section.id} style={{ marginTop: 8 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: "rgba(0, 0, 0, 0.88)", marginBottom: 8 }}>
                    {section.title}
                  </div>
                  <ReviewFieldGrid fields={reviewFields} darkLabels />
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <div>
        <div className="flex items-center justify-between gap-12 mb-12">
          <Title level={5} style={{ margin: 0 }}>
            Tickets
          </Title>
          <Tag>{event.tickets?.length ?? 0} ticket types</Tag>
        </div>
        {event.tickets?.length ? (
          <div className="flex flex-col gap-12">
            {event.tickets.map((ticket, index) => (
              <TicketReviewCard
                key={ticket.id || ticket.name || index}
                ticket={ticket}
                eventCurrency={event.currency}
                eventType={event.event_type}
                index={index}
              />
            ))}
          </div>
        ) : (
          <Card
            size="small"
            className={styles.reviewSectionCard}
            styles={CARD_HEAD_STYLES}
          >
            <Text type="secondary">No tickets configured</Text>
          </Card>
        )}
      </div>
    </div>
  );
};

export default EventSetupReviewSummary;
